import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatShortDate } from '../../../utils/dateUtils';

const CityTableRow = ({ 
  city, 
  isSelected, 
  onSelect, 
  onEdit, 
  onDelete, 
  onViewMap,
  currentLanguage = 'ar' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getCityName = () => {
    switch (currentLanguage) {
      case 'en':
        return city?.nameEn || city?.nameAr;
      case 'tr':
        return city?.nameTr || city?.nameAr;
      default:
        return city?.nameAr;
    }
  };

  const getStatusColor = () => {
    if (city?.subUnionCount === 0) return 'text-muted-foreground';
    if (city?.subUnionCount >= 5) return 'text-success';
    return 'text-warning';
  };

  return (
    <>
      <tr className={`border-b border-border hover:bg-muted/50 transition-colors ${
        isSelected ? 'bg-accent/10' : ''
      }`}>
        {/* Selection Checkbox */}
        <td className="w-12 px-4 py-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(city?.id, e?.target?.checked)}
            className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
          />
        </td>

        {/* City Name */}
        <td className="px-4 py-3">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-6 h-6 rounded border border-border bg-muted text-xs font-medium text-foreground">
              {city?.colorZone || 0}
            </div>
            <div>
              <p className="font-medium text-foreground">{getCityName()}</p>
              {currentLanguage !== 'ar' && (
                <p className="text-xs text-muted-foreground">{city?.nameAr}</p>
              )}
            </div>
          </div>
        </td>

        {/* Sub-Union Count */}
        <td className="px-4 py-3">
          <div className="flex items-center space-x-2">
            <span className={`font-medium ${getStatusColor()}`}>
              {city?.subUnionCount}
            </span>
            <Icon name="Building2" size={14} className="text-muted-foreground" />
          </div>
        </td>

        {/* Coordinates */}
        <td className="px-4 py-3">
          <div className="text-sm text-muted-foreground font-mono">
            <div>X: {city?.svgX}</div>
            <div>Y: {city?.svgY}</div>
          </div>
        </td>

        {/* Status */}
        <td className="px-4 py-3">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            city?.isActive 
              ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
          }`}>
            <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
              city?.isActive ? 'bg-success' : 'bg-muted-foreground'
            }`} />
            {city?.isActive ? 'نشط' : 'غير نشط'}
          </span>
        </td>

        {/* Created Date */}
        <td className="px-4 py-3 text-sm text-muted-foreground">
          {formatShortDate(city?.createdAt)}
        </td>

        {/* Actions */}
        <td className="px-4 py-3">
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              className="w-8 h-8 text-muted-foreground hover:text-foreground"
              onClick={() => onViewMap(city)}
              iconName="MapPin"
              iconSize={14}
              title="عرض على الخريطة"
            />
            
            <Button
              variant="ghost"
              size="icon"
              className="w-8 h-8 text-muted-foreground hover:text-foreground"
              onClick={() => onEdit(city)}
              iconName="Edit3"
              iconSize={14}
              title="تحرير"
            />
            
            <Button
              variant="ghost"
              size="icon"
              className="w-8 h-8 text-muted-foreground hover:text-error"
              onClick={() => onDelete(city)}
              iconName="Trash2"
              iconSize={14}
              title="حذف"
            />
            
            <Button
              variant="ghost"
              size="icon"
              className="w-8 h-8 text-muted-foreground hover:text-foreground lg:hidden"
              onClick={() => setIsExpanded(!isExpanded)}
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              iconSize={14}
              title="تفاصيل أكثر"
            />
          </div>
        </td>
      </tr>
      {/* Mobile Expanded Details */}
      {isExpanded && (
        <tr className="lg:hidden border-b border-border bg-muted/30">
          <td colSpan="7" className="px-4 py-3">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">الإحداثيات:</span>
                  <div className="font-mono">X: {city?.svgX}, Y: {city?.svgY}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">منطقة اللون:</span>
                  <div className="mt-1">
                    <span className="text-xs font-medium">{city?.colorZone || 0}</span>
                  </div>
                </div>
              </div>
              
              {city?.nameEn && (
                <div>
                  <span className="text-muted-foreground text-sm">الاسم بالإنجليزية:</span>
                  <div>{city?.nameEn}</div>
                </div>
              )}
              
              {city?.nameTr && (
                <div>
                  <span className="text-muted-foreground text-sm">الاسم بالتركية:</span>
                  <div>{city?.nameTr}</div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default CityTableRow;